import { create } from "zustand";
import { persist } from "zustand/middleware";
import { jwtDecode } from "jwt-decode";

interface Auth {
  isAuthenticated: boolean;
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  name?: string;
  id?: string;
  role?: string;
  clientId?: string;
  tokenExpiry?: number;
}

interface AuthStore extends Auth {
  setAuth: (auth: Partial<Auth> | ((auth: Auth) => Partial<Auth>)) => void;
  setTokens: (
    accessToken: string,
    refreshToken?: string,
    expiresIn?: number,
  ) => void;
  clearTokens: () => void;
  clear: () => void;
  isTokenValid: () => boolean;
  getUserInfo: () => Pick<Auth, "email" | "name" | "id" | "role" | "clientId">;
  getAuthHeader: () => string | null;
  refreshAuthToken: () => Promise<boolean>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      accessToken: undefined,
      refreshToken: undefined,
      email: undefined,
      name: undefined,
      id: undefined,
      role: undefined,
      clientId: undefined,
      tokenExpiry: undefined,

      // Set auth data (partial updates)
      setAuth: (auth: Partial<Auth> | ((auth: Auth) => Partial<Auth>)) => {
        const newAuth = typeof auth === "function" ? auth(get()) : auth;
        set((state) => ({ ...state, ...newAuth }));

        // Keep localStorage in sync for interceptor/revalidation
        if (newAuth.accessToken) {
          localStorage.setItem("accesstoken", newAuth.accessToken);
        }
        if (newAuth.id) {
          localStorage.setItem("id", newAuth.id);
        }
      },

      // Set tokens with automatic expiry calculation
      setTokens: (
        accessToken: string,
        refreshToken?: string,
        expiresIn?: number,
      ) => {
        const tokenExpiry = expiresIn
          ? Date.now() + expiresIn * 1000
          : undefined;

        // Decode token to extract user info
        try {
          const decodedToken = jwtDecode(accessToken) as {
            exp: number;
            id: string;
            role?: string;
            email?: string;
            clientId?: string;
          };

          set((state) => ({
            ...state,
            accessToken,
            refreshToken: refreshToken || state.refreshToken,
            tokenExpiry: tokenExpiry || decodedToken.exp * 1000,
            isAuthenticated: true,
            id: decodedToken.id,
            role: decodedToken.role,
            email: decodedToken.email,
            clientId: decodedToken.clientId,
          }));
        } catch (error) {
          // Fallback if token decode fails
          set((state) => ({
            ...state,
            accessToken,
            refreshToken: refreshToken || state.refreshToken,
            tokenExpiry,
            isAuthenticated: true,
          }));
        }

        // Store in localStorage for axios interceptor
        localStorage.setItem("accesstoken", accessToken);
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        }
      },

      // Clear only tokens (also cleans up all related localStorage keys)
      clearTokens: () => {
        localStorage.removeItem("accesstoken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("id");
        set((state) => ({
          ...state,
          accessToken: undefined,
          refreshToken: undefined,
          tokenExpiry: undefined,
          isAuthenticated: false,
        }));
      },

      // Clear all auth data
      clear: () => {
        localStorage.removeItem("accesstoken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("id");
        set(() => ({
          isAuthenticated: false,
          accessToken: undefined,
          refreshToken: undefined,
          email: undefined,
          name: undefined,
          role: undefined,
          id: undefined,
          clientId: undefined,
          tokenExpiry: undefined,
        }));
      },

      // Check if current token is valid
      isTokenValid: () => {
        const state = get();
        if (!state.accessToken || !state.tokenExpiry) return false;

        // Check if token is expired (with 5 minute buffer)
        const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
        return state.tokenExpiry > Date.now() + bufferTime;
      },

      // Get user information
      getUserInfo: () => {
        const state = get();
        return {
          email: state.email,
          name: state.name,
          id: state.id,
          role: state.role,
          clientId: state.clientId,
        };
      },

      // Get authorization header for API calls
      getAuthHeader: () => {
        const state = get();
        return state.accessToken ? `Bearer ${state.accessToken}` : null;
      },

      // Refresh token (placeholder for future implementation)
      refreshAuthToken: async () => {
        const state = get();
        if (!state.refreshToken) return false;

        // TODO: Implement refresh token logic when backend supports it
        // For now, return false to trigger re-login
        return false;
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        email: state.email,
        name: state.name,
        id: state.id,
        role: state.role,
        clientId: state.clientId,
        tokenExpiry: state.tokenExpiry,
      }),
    },
  ),
);
